'use client'

import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import './lakbay-case-study.css'

/**
 * Lakbay AI case study.
 *
 * A direct port of `docs/design-references/pixelmojo-lakbay-ai-case-study.html`.
 * That
 * file is the design contract: markup, class names, and section order match it
 * one-for-one so the two can be diffed. Styling lives in the sibling
 * `lakbay-case-study.css`, scoped under `.lakbay-cs`.
 *
 * This renders full-bleed — `WorkPage`'s hero and container are skipped for
 * this slug (see `fullBleedProjects` in ./index.ts) because the case study
 * ships its own hero. The site header and footer are unchanged around it; the
 * source file's own nav and footer were dropped in the port.
 *
 * One deliberate break from the source: its top scroll-progress bar was lifted
 * out to `@/components/ScrollProgress` and now renders site-wide from the root
 * layout, so the local copy is gone. The side rail stays — it tracks chapters,
 * which nothing outside this page has.
 */

const PORTALS = [
  {
    id: 'traveler',
    num: '01',
    name: 'Traveler',
    tagline: 'Personal trip planning',
    label: 'Traveler portal',
    heading: 'From wish to working route.',
    copy: 'Create, explore, and keep a personal itinerary with day-level structure and practical travel context.',
    boardLabel: 'Stylized traveler portal preview',
    brand: 'Lakbay AI',
    status: 'ROUTE READY',
  },
  {
    id: 'agent',
    num: '02',
    name: 'Agent',
    tagline: 'Guided client workflows',
    label: 'Agent portal',
    heading: 'More clients. Less repetition.',
    copy: 'A guided workspace for building and managing routes around client preferences.',
    boardLabel: 'Stylized travel agent portal preview',
    brand: 'Lakbay / Agent',
    status: 'CLIENT ROUTES',
  },
  {
    id: 'admin',
    num: '03',
    name: 'Admin',
    tagline: 'Manage the system',
    label: 'Admin portal',
    heading: 'See and steer the system.',
    copy: 'Role-gated controls for the information, activity, and users behind the experience.',
    boardLabel: 'Stylized administration portal preview',
    brand: 'Lakbay / Admin',
    status: 'SYSTEM VIEW',
  },
] as const

const STEPS = [
  {
    num: '01 / ASK',
    title: 'Describe the trip',
    copy: 'Destination, duration, budget, interests—or simply the mood.',
  },
  {
    num: '02 / SHAPE',
    title: 'Build the route',
    copy: 'Relevant local knowledge becomes a usable day-by-day plan.',
  },
  {
    num: '03 / GO',
    title: 'Explore the days',
    copy: 'Activities, timing, costs, locations, and helpful travel notes.',
  },
]

const FLOW = [
  {
    step: '01 / Intent',
    title: 'Ask naturally',
    copy: 'Wizard inputs or a free-form travel request.',
  },
  {
    step: '02 / Retrieve',
    title: 'Find context',
    copy: 'Vector search surfaces relevant local knowledge.',
  },
  {
    step: '03 / Generate',
    title: 'Shape a route',
    copy: 'The response is streamed into structured day cards.',
  },
  {
    step: '04 / Extend',
    title: 'Search + save',
    copy: 'Flight intent can trigger Amadeus; signed-in users keep trips.',
  },
]

const LAYERS = [
  {
    index: 'LAYER 01',
    name: 'Experience',
    tools: ['Next.js 16', 'TypeScript'],
  },
  { index: 'LAYER 02', name: 'Intelligence', tools: ['OpenAI', 'pgvector'] },
  { index: 'LAYER 03', name: 'Travel search', tools: ['Amadeus API'] },
  { index: 'LAYER 04', name: 'Identity', tools: ['Clerk', 'Role routing'] },
  { index: 'LAYER 05', name: 'Content', tools: ['MDX', '13 articles'] },
]

const METRICS = [
  {
    label: 'Time to itinerary',
    value: '<60s',
    word: false,
    copy: 'From travel intent to a structured first route.',
  },
  {
    label: 'Knowledge coverage',
    value: '18',
    word: false,
    copy: 'Mapped destinations with structured local content.',
  },
  {
    label: 'Connected access',
    value: '3',
    word: false,
    copy: 'Traveler, travel-agent, and admin portals.',
  },
  {
    label: 'Planning cost',
    value: 'Free',
    word: true,
    copy: 'The public planning experience is free to try.',
  },
  {
    label: 'First interaction',
    value: 'No signup',
    word: true,
    copy: 'Start a route before creating an account.',
  },
  {
    label: 'Product context',
    value: '7,641',
    word: false,
    copy: 'Islands behind one deceptively simple prompt.',
  },
]

const MAP_POINTS = [
  { cx: 342, cy: 20, r: 10, major: true, label: 'Batanes', tx: 360, ty: 24 },
  { cx: 294, cy: 150, r: 8, major: false, label: 'Manila', tx: 313, ty: 154 },
  { cx: 230, cy: 250, r: 8, major: false, label: 'Boracay', tx: 136, ty: 254 },
  { cx: 356, cy: 372, r: 10, major: true, label: 'Cebu', tx: 374, ty: 376 },
  { cx: 220, cy: 485, r: 8, major: false, label: 'El Nido', tx: 133, ty: 489 },
  { cx: 443, cy: 548, r: 10, major: true, label: 'Siargao', tx: 359, ty: 552 },
]

const MAP_DOTS = [
  [310, 76],
  [262, 198],
  [282, 292],
  [398, 335],
  [315, 401],
  [247, 444],
  [271, 512],
  [344, 520],
  [388, 469],
  [177, 338],
  [410, 281],
  [205, 122],
]

export default function LakbayAIProjectContent() {
  const rootRef = useRef<HTMLDivElement>(null)
  const railFillRef = useRef<HTMLSpanElement>(null)
  const railMarkerRef = useRef<HTMLSpanElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const [mounted, setMounted] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [chapter, setChapter] = useState('Overview')
  const [activePortal, setActivePortal] = useState(0)

  // Enables the reveal transitions only once JS is running, so the
  // server-rendered markup is never stuck at opacity 0.
  useEffect(() => setMounted(true), [])

  // The hero is drawn to fill one viewport. Anything the site renders above
  // this component (the sticky header) has to come off that height, or the
  // bottom of the hero sits below the fold. Measured rather than hardcoded so
  // it survives header height changes across breakpoints.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const measure = () => {
      const offset = root.getBoundingClientRect().top + window.scrollY
      root.style.setProperty('--chrome-offset', `${Math.max(0, offset)}px`)
    }

    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // Side rail and current-chapter label.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const chapters = Array.from(
      root.querySelectorAll<HTMLElement>('[data-chapter]')
    )

    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      const ratio = max > 0 ? Math.min(1, window.scrollY / max) : 0

      if (railFillRef.current) {
        railFillRef.current.style.height = `${ratio * 100}%`
      }
      if (railMarkerRef.current) {
        railMarkerRef.current.style.top = `${ratio * 100}%`
      }

      setScrolled(window.scrollY > window.innerHeight * 0.7)

      let current = chapters[0]
      chapters.forEach(section => {
        if (section.getBoundingClientRect().top <= window.innerHeight * 0.45) {
          current = section
        }
      })
      if (current?.dataset.chapter) setChapter(current.dataset.chapter)
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  // Reveal-on-scroll.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const targets = root.querySelectorAll<HTMLElement>('[data-reveal]')
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('visible'))
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' }
    )

    targets.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const onTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const keys = [
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ]
      if (!keys.includes(event.key)) return
      event.preventDefault()

      let next = index
      if (event.key === 'Home') next = 0
      else if (event.key === 'End') next = PORTALS.length - 1
      else {
        const delta =
          event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1
        next = (index + delta + PORTALS.length) % PORTALS.length
      }

      setActivePortal(next)
      tabRefs.current[next]?.focus()
    },
    []
  )

  return (
    <div
      ref={rootRef}
      className={`lakbay-cs${mounted ? ' js' : ''}${scrolled ? ' scrolled' : ''}`}
    >
      <aside className='case-rail' aria-hidden='true'>
        <span className='rail-label'>{chapter}</span>
        <div className='rail-track'>
          <span className='rail-fill' ref={railFillRef} />
          <span className='rail-marker' ref={railMarkerRef} />
        </div>
      </aside>

      {/* ---------- Hero ---------- */}
      <section className='hero chapter' id='overview' data-chapter='Overview'>
        <div className='hero-image' aria-hidden='true' />
        <div className='hero-noise' aria-hidden='true' />
        <svg className='hero-route' viewBox='0 0 800 800' aria-hidden='true'>
          <path d='M745 45C570 120 650 226 498 280S295 348 383 440s206 88 124 178S312 660 165 770' />
          <circle cx='745' cy='45' r='9' />
          <circle cx='498' cy='280' r='9' />
          <circle className='origin' cx='383' cy='440' r='13' />
          <circle cx='507' cy='618' r='9' />
          <circle cx='165' cy='770' r='9' />
        </svg>
        <div className='wrap hero-inner'>
          <div className='eyebrow hero-kicker'>
            Pixelmojo × Lakbay AI · 2026
          </div>
          <h1 className='display'>
            <span>We turned</span>
            <span className='islands'>7,641 islands</span>
            <span className='conversation'>into one conversation.</span>
          </h1>
          <div className='hero-meta'>
            <p className='hero-intro'>
              A local-minded AI travel concierge that turns one sentence into a
              personalized Philippine route—in under 60 seconds.
            </p>
            <div className='hero-details'>
              <div className='detail'>
                <span className='mono'>Discipline</span>
                <b>AI product design</b>
              </div>
              <div className='detail'>
                <span className='mono'>Coverage</span>
                <b>18 destinations</b>
              </div>
              <div className='detail'>
                <span className='mono'>Experience</span>
                <b>3 connected portals</b>
              </div>
              <div className='detail'>
                <span className='mono'>Studio</span>
                <b>Makati, Philippines</b>
              </div>
            </div>
          </div>
          <div className='hero-bottom'>
            <a className='mono' href='#brief'>
              Follow the route <i>↓</i>
            </a>
            <div className='hero-actions'>
              <span className='mono'>Case study / AI travel tech</span>
              <a
                className='hero-cta'
                href='https://travel-conceirge.vercel.app/'
                target='_blank'
                rel='noreferrer'
              >
                <span>Try Lakbay AI</span>
                <span aria-hidden='true'>↗</span>
              </a>
            </div>
          </div>
          <span className='hero-index' aria-hidden='true'>
            AI
          </span>
        </div>
      </section>

      {/* ---------- Product window ---------- */}
      <section
        className='project-window-section'
        aria-label='Lakbay AI product preview'
      >
        <div className='project-window' data-reveal>
          <div className='window-bar'>
            <span className='window-dots' aria-hidden='true'>
              <i />
              <i />
              <i />
            </span>
            <span className='window-url'>
              LAKBAY AI / YOUR 7,641-ISLAND CO-PILOT
            </span>
            <span className='mono'>Live product</span>
          </div>
          <Image
            src='/projects/lakbay-ai/homepage.webp'
            alt='Lakbay AI homepage showing an island route, trip prompt, and itinerary preview'
            width={1440}
            height={1100}
            priority
          />
          <div className='window-caption'>
            <span>One prompt replaces the blank itinerary.</span>
            <span className='mono'>Strategy · Experience · Engineering</span>
          </div>
        </div>
      </section>

      {/* ---------- 01 The brief ---------- */}
      <section className='brief chapter' id='brief' data-chapter='The brief'>
        <span className='brief-number' aria-hidden='true'>
          7,641
        </span>
        <div className='wrap grid'>
          <div className='section-label' data-reveal>
            <span className='eyebrow'>01 · The brief</span>
          </div>
          <div className='section-copy' data-reveal>
            <h2 className='display section-title'>
              The Philippines is not one destination. It is{' '}
              <span className='accent'>7,641 possibilities.</span>
            </h2>
            <p className='lead'>
              The product had to make an enormous archipelago feel approachable
              from the very first question.
            </p>
            <div className='body-copy'>
              <p>
                Trip planning was scattered across old articles, booking tabs,
                maps, and disconnected advice. Travelers were doing the hard
                integration work themselves before the holiday even began.
              </p>
              <p>
                Lakbay needed to understand mood, pace, budget, and
                interests—then shape that intent into a route without forcing
                anyone to learn another complicated planning tool.
              </p>
            </div>
          </div>
          <div className='brief-tension' data-reveal>
            <div className='tension-item'>
              <span className='mono'>Before</span>
              <strong>Hours of tabs</strong>
            </div>
            <div className='tension-arrow' aria-hidden='true'>
              →
            </div>
            <div className='tension-item'>
              <span className='mono'>After</span>
              <strong>One natural prompt</strong>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 02 The experience ---------- */}
      <section
        className='experience chapter'
        id='experience'
        data-chapter='Experience'
      >
        <div className='wrap grid'>
          <div className='experience-header' data-reveal>
            <div>
              <span className='eyebrow'>02 · The experience</span>
              <h2 className='display'>
                Say it naturally. Lakbay does the sorting.
              </h2>
            </div>
            <p>
              The interaction starts where travelers already are: “Here’s the
              kind of trip I want.” Lakbay returns a structured route that is
              ready to explore, adjust, and save.
            </p>
          </div>
          <div className='prompt-stage' data-reveal>
            <div className='prompt-card'>
              <div className='prompt-text'>
                Five days. Surf, café mornings, and island hopping—easy pace.
              </div>
              <span className='prompt-send' aria-hidden='true'>
                →
              </span>
            </div>
            <div className='dialog-frame'>
              <Image
                src='/projects/lakbay-ai/itinerary.webp'
                alt='Lakbay AI structured Siargao itinerary with daily activities, route, pace, and budget'
                width={1440}
                height={1100}
                loading='lazy'
              />
            </div>
          </div>
          <div className='experience-steps' data-reveal>
            {STEPS.map(step => (
              <article className='step' key={step.num}>
                <span className='num'>{step.num}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
          <div className='micro-strip mono' data-reveal>
            <span>Free to plan</span>
            <span>No signup required</span>
            <span>First route in &lt;60 sec</span>
          </div>
        </div>
      </section>

      {/* ---------- 03 The intelligence ---------- */}
      <section
        className='intelligence chapter'
        id='intelligence'
        data-chapter='Intelligence'
      >
        <div className='wrap grid'>
          <div className='intelligence-copy' data-reveal>
            <span className='eyebrow'>03 · The intelligence</span>
            <h2 className='display'>Local knowledge, made retrievable.</h2>
            <p>
              A retrieval layer connects each request with structured travel
              content across 18 mapped destinations. The model gets relevant
              context before it shapes the answer.
            </p>
            <div
              className='stack-tags'
              aria-label='Selected intelligence stack'
            >
              <span>OpenAI</span>
              <span>Supabase pgvector</span>
              <span>Structured content</span>
            </div>
          </div>
          <div className='map-card' data-reveal>
            <span className='map-title mono'>Mapped destination layer</span>
            <span className='map-count'>18</span>
            <svg
              className='archipelago'
              viewBox='0 0 540 570'
              role='img'
              aria-label='Abstract route through mapped Philippine destinations'
            >
              <path d='M342 20C267 67 358 101 294 150S169 192 230 250s173 45 126 122S182 409 220 485 368 488 443 548' />
              {MAP_POINTS.map(point => (
                <g key={point.label}>
                  <circle
                    className={point.major ? 'major' : undefined}
                    cx={point.cx}
                    cy={point.cy}
                    r={point.r}
                  />
                  <text x={point.tx} y={point.ty}>
                    {point.label}
                  </text>
                </g>
              ))}
              {MAP_DOTS.map(([cx, cy]) => (
                <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={5} />
              ))}
            </svg>
          </div>
          <div
            className='system-flow'
            data-reveal
            aria-label='Lakbay intelligence flow'
          >
            {FLOW.map(node => (
              <article className='flow-node' key={node.step}>
                <span className='mono'>{node.step}</span>
                <b>{node.title}</b>
                <p>{node.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- 04 Connected product ---------- */}
      <section
        className='portals chapter'
        id='portals'
        data-chapter='Three portals'
      >
        <div className='wrap grid'>
          <div className='portals-header' data-reveal>
            <div>
              <span className='eyebrow'>04 · Connected product</span>
              <h2 className='display'>
                One intelligence layer. Three ways in.
              </h2>
            </div>
            <p>
              Lakbay is not a single chat screen. It connects travelers, travel
              agents, and administrators through role-shaped workflows.
            </p>
          </div>
          <div className='portal-shell' data-reveal>
            <div
              className='portal-tabs'
              role='tablist'
              aria-label='Lakbay user portals'
            >
              {PORTALS.map((portal, index) => (
                <button
                  key={portal.id}
                  ref={element => {
                    tabRefs.current[index] = element
                  }}
                  className='portal-tab'
                  id={`tab-${portal.id}`}
                  role='tab'
                  type='button'
                  aria-selected={activePortal === index}
                  aria-controls={`panel-${portal.id}`}
                  tabIndex={activePortal === index ? 0 : -1}
                  onClick={() => setActivePortal(index)}
                  onKeyDown={event => onTabKeyDown(event, index)}
                >
                  <span className='tab-num'>{portal.num}</span>
                  <strong>{portal.name}</strong>
                  <span>{portal.tagline}</span>
                </button>
              ))}
            </div>
            <div className='portal-canvas'>
              {PORTALS.map((portal, index) => (
                <section
                  key={portal.id}
                  className={`portal-panel${activePortal === index ? ' active' : ''}`}
                  id={`panel-${portal.id}`}
                  role='tabpanel'
                  aria-labelledby={`tab-${portal.id}`}
                  hidden={activePortal !== index}
                >
                  <div className='portal-copy'>
                    <span className='mono'>{portal.label}</span>
                    <h3>{portal.heading}</h3>
                    <p>{portal.copy}</p>
                  </div>
                  <div className='ui-board' aria-label={portal.boardLabel}>
                    <div className='ui-top'>
                      <span className='ui-brand'>{portal.brand}</span>
                      <span className='ui-status'>{portal.status}</span>
                    </div>
                    <div className='ui-content'>
                      <div className='ui-side'>
                        <i />
                        <i />
                        <i />
                        <i />
                      </div>
                      <div className='ui-main'>
                        {portal.id === 'traveler' && (
                          <>
                            <div className='ui-hero' />
                            <div className='ui-row'>
                              <div className='ui-card'>
                                <b>Day 01 · Arrival</b>
                                <i />
                                <i />
                              </div>
                              <div className='ui-card'>
                                <b>Day 02 · Explore</b>
                                <i />
                                <i />
                              </div>
                            </div>
                          </>
                        )}
                        {portal.id === 'agent' && (
                          <>
                            <div className='ui-row'>
                              <div className='ui-card'>
                                <b>Client brief</b>
                                <i />
                                <i />
                              </div>
                              <div className='ui-card'>
                                <b>Route status</b>
                                <i />
                                <i />
                              </div>
                            </div>
                            <div
                              className='ui-hero'
                              style={{
                                background: 'var(--sun)',
                                borderColor: 'var(--coral)',
                              }}
                            />
                          </>
                        )}
                        {portal.id === 'admin' && (
                          <>
                            <div
                              className='ui-hero'
                              style={{
                                background: 'var(--mangrove)',
                                borderColor: 'var(--cyan)',
                              }}
                            />
                            <div className='ui-row'>
                              <div className='ui-card'>
                                <b>Conversations</b>
                                <i />
                                <i />
                              </div>
                              <div className='ui-card'>
                                <b>Destinations</b>
                                <i />
                                <i />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 05 The build ---------- */}
      <section className='system chapter' id='system' data-chapter='The build'>
        <div className='wrap grid'>
          <div className='system-header' data-reveal>
            <div>
              <span className='eyebrow'>05 · The build</span>
              <h2 className='display'>
                Complex underneath. Effortless at the surface.
              </h2>
            </div>
            <p>
              Generation, retrieval, travel search, identity, and three user
              experiences operate as one coherent product system.
            </p>
          </div>
          <div className='layers' data-reveal>
            {LAYERS.map(layer => (
              <article className='layer' key={layer.index}>
                <span className='layer-index'>{layer.index}</span>
                <h3>{layer.name}</h3>
                <div className='layer-tools'>
                  {layer.tools.map(tool => (
                    <span key={tool}>{tool}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
          <p className='build-note' data-reveal>
            Every technical decision serves the same product idea: planning the
            Philippines should feel smaller than the country really is.
          </p>
        </div>
      </section>

      {/* ---------- 06 Visual system ---------- */}
      <section
        className='visual-system chapter'
        id='design'
        data-chapter='Visual system'
      >
        <div className='wrap grid'>
          <div className='visual-head' data-reveal>
            <div>
              <span className='eyebrow'>06 · Visual system</span>
              <h2 className='display'>
                Designed like a route, not a dashboard.
              </h2>
            </div>
            <p>
              Ferry-map logic, Philippine travel color, and confident editorial
              type make the interface useful before it becomes decorative.
            </p>
          </div>
          <div className='design-board' data-reveal>
            <div className='mobile-showcase'>
              <div className='mobile-phone'>
                <Image
                  src='/projects/lakbay-ai/mobile.webp'
                  alt='Mobile Lakbay AI interface'
                  width={390}
                  height={844}
                  loading='lazy'
                />
              </div>
            </div>
            <div className='tokens'>
              <div className='palette'>
                <span className='mono'>Project palette</span>
                <div
                  className='swatches'
                  aria-label='Deep Channel, Pacific Cyan, Sunburst, Hibiscus, and Mangrove'
                >
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
                <div className='palette-labels'>
                  <span>CHANNEL</span>
                  <span>PACIFIC</span>
                  <span>SUN</span>
                  <span>HIBISCUS</span>
                  <span>MANGROVE</span>
                </div>
              </div>
              <div className='type-card'>
                <span className='mono'>Voice / Direct, warm, useful</span>
                <div className='type-sample'>
                  TARA.
                  <br />
                  LET&apos;S GO.
                </div>
                <span className='mono'>
                  Anybody × Familjen Grotesk × DM Mono
                </span>
              </div>
            </div>
            <div className='route-grammar'>
              <span className='mono'>
                Signature grammar / a route always goes somewhere
              </span>
              <svg viewBox='0 0 1100 150' aria-hidden='true'>
                <path d='M-20 125C180 25 260 140 425 72S710 10 800 86s160 34 330-40' />
                <circle cx='425' cy='72' r='12' />
                <circle cx='800' cy='86' r='12' />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 07 Product scope ---------- */}
      <section
        className='shipped chapter'
        id='shipped'
        data-chapter='What shipped'
      >
        <div className='wrap grid'>
          <div className='shipped-header' data-reveal>
            <div>
              <span className='eyebrow'>07 · Product scope</span>
              <h2 className='display'>
                A smaller planning surface for a very large country.
              </h2>
            </div>
            <p>
              These are shipped product facts—not dressed-up vanity metrics. The
              result is a broad platform with a remarkably simple front door.
            </p>
          </div>
          <div className='metrics' data-reveal>
            {METRICS.map(metric => (
              <article className='metric' key={metric.label}>
                <span className='mono'>{metric.label}</span>
                <span className={`value${metric.word ? ' word' : ''}`}>
                  {metric.value}
                </span>
                <p>{metric.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Closing ---------- */}
      <section
        className='closing chapter'
        id='contact'
        data-chapter='Build with us'
      >
        <svg className='closing-route' viewBox='0 0 700 400' aria-hidden='true'>
          <path d='M720 360C560 270 620 155 465 180S285 320 185 245 80 80-30 44' />
        </svg>
        <div className='wrap closing-inner'>
          <span className='eyebrow'>Pixelmojo · AI product studio</span>
          <h2 className='display'>
            Have a hard product that should feel this simple?
          </h2>
          <div className='closing-actions'>
            <a className='button primary' href='mailto:founders@pixelmojo.io'>
              <span>Build with Pixelmojo</span>
              <span>→</span>
            </a>
            <a
              className='button secondary'
              href='https://travel-conceirge.vercel.app/'
              target='_blank'
              rel='noreferrer'
            >
              <span>Try Lakbay AI</span>
              <span>↗</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
